/**
 * A deploy replaces the hashed files under /assets, so a tab still running the
 * previous bundle 404s the moment it lazily imports a chunk it had not already
 * fetched. Browsers surface that as a plain `TypeError` with no error code, so
 * matching the message is the only option available.
 *
 * `ChunkLoadError` is webpack's name for the same failure. It cannot fire in a
 * Vite build, but it is kept here so the helper stays correct for any bundle
 * that is still built by webpack.
 */
const STALE_CHUNK_ERROR_NAMES = ['ChunkLoadError'];

const STALE_CHUNK_MESSAGE_FRAGMENTS = [
  // Chrome, Edge
  'failed to fetch dynamically imported module',
  // Safari
  'importing a module script failed',
  'error loading dynamically imported module',
  // Firefox
  'error loading a module script',
  // Any browser when the 404 serves the SSR HTML shell instead of JavaScript
  'failed to load module script',
  'expected a javascript-or-wasm module script',
  // Vite's CSS preload helper
  'unable to preload css',
];

type ErrorLike = { name?: unknown; message?: unknown };

const readErrorFields = (error: unknown): ErrorLike | null =>
  typeof error === 'object' && error !== null ? (error as ErrorLike) : null;

/**
 * Whether this error means "the chunk I asked for is no longer deployed".
 *
 * Deliberately duck-typed rather than using `instanceof Error`: the error
 * crosses a dynamic-import boundary and can arrive wrapped or from another
 * realm, where `instanceof` silently returns false.
 */
export const isStaleChunkError = (error: unknown): boolean => {
  const fields = readErrorFields(error);
  if (!fields) return false;

  if (
    typeof fields.name === 'string' &&
    STALE_CHUNK_ERROR_NAMES.includes(fields.name)
  ) {
    return true;
  }

  if (typeof fields.message !== 'string') return false;

  const message = fields.message.toLowerCase();
  return STALE_CHUNK_MESSAGE_FRAGMENTS.some((fragment) =>
    message.includes(fragment),
  );
};
